import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import { Building2, ShieldAlert } from 'lucide-react';

const EMPTY = { holder_name: '', doc_number: '', bank: '', account_type: 'ahorros', account_number: '' };

/**
 * Seller KYC — Bank account for automated payouts (FASE C).
 * Never renders the raw account_number back; backend returns a masked value.
 */
export function SellerBankAccountSection() {
  const [current, setCurrent] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/sellers/me/bank-account');
      setCurrent(data);
    } catch {
      setCurrent({ bank_account: {}, complete: false });
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.holder_name || !form.doc_number || !form.bank || !form.account_number) {
      toast.error('Completa todos los campos');
      return;
    }
    setSaving(true);
    try {
      await api.put('/sellers/me/bank-account', form);
      toast.success('Datos bancarios actualizados');
      setEditing(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (!current) return null;
  const b = current.bank_account || {};
  const complete = current.complete;

  return (
    <section className="mt-8 border border-slate-200 bg-white p-5" data-testid="seller-bank-account-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">KYC · Pagos automáticos</div>
          <h2 className="font-display text-lg font-black tracking-tighter text-slate-900 mt-1 inline-flex items-center gap-2">
            <Building2 className="h-4 w-4" /> Datos bancarios
          </h2>
        </div>
        <div className="text-xs">
          {complete ? (
            <span className="text-emerald-700 font-bold">Completos</span>
          ) : (
            <span className="text-amber-700 font-bold inline-flex items-center gap-1">
              <ShieldAlert className="h-3.5 w-3.5" /> Pendientes — los payouts quedan en HELD
            </span>
          )}
        </div>
      </div>

      {!editing ? (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <Field label="Titular" value={b.holder_name || '—'} />
          <Field label="Documento" value={b.doc_number || '—'} />
          <Field label="Banco" value={b.bank || '—'} />
          <Field label="Tipo de cuenta" value={b.account_type ? (b.account_type === 'ahorros' ? 'Ahorros' : 'Corriente') : '—'} />
          <Field label="Número de cuenta" value={b.account_number_masked || '—'} className="sm:col-span-2 font-mono" />
          <div className="sm:col-span-2">
            <Button
              data-testid="seller-bank-edit-btn"
              onClick={() => { setForm({ ...EMPTY, ...(b.holder_name ? { holder_name: b.holder_name, doc_number: b.doc_number, bank: b.bank, account_type: b.account_type } : {}) }); setEditing(true); }}
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
            >
              {complete ? 'Actualizar' : 'Agregar datos bancarios'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input data-testid="seller-bank-holder" placeholder="Titular de la cuenta" value={form.holder_name}
            onChange={(e) => setForm({ ...form, holder_name: e.target.value })} className="rounded-none" />
          <Input data-testid="seller-bank-doc" placeholder="Cédula / NIT" value={form.doc_number}
            onChange={(e) => setForm({ ...form, doc_number: e.target.value })} className="rounded-none" />
          <Input data-testid="seller-bank-name" placeholder="Banco" value={form.bank}
            onChange={(e) => setForm({ ...form, bank: e.target.value })} className="rounded-none" />
          <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
            <SelectTrigger data-testid="seller-bank-type" className="rounded-none"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ahorros">Ahorros</SelectItem>
              <SelectItem value="corriente">Corriente</SelectItem>
            </SelectContent>
          </Select>
          <Input data-testid="seller-bank-number" placeholder="Número de cuenta" value={form.account_number}
            onChange={(e) => setForm({ ...form, account_number: e.target.value })} className="rounded-none sm:col-span-2 font-mono" />
          <div className="flex gap-2 sm:col-span-2">
            <Button data-testid="seller-bank-save-btn" disabled={saving} onClick={save}
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white">
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
            <Button variant="outline" onClick={() => { setEditing(false); setForm(EMPTY); }} className="rounded-none">
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function Field({ label, value, className = '' }) {
  return (
    <div className={className}>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
